<template>
    <VModalWrapper :name="name" :title="title" v-on="listeners" v-bind="attributes">
        <ValidationObserver v-slot="{ handleSubmit }" v-if="attribute">
            <form class="active-form" @submit.prevent="handleSubmit(submit)">
                <ValidationProvider
                    class="active-form__field"
                    name="type"
                    rules="required|max:64"
                    v-slot="{ errors }"
                    v-if="visibleFields.type"
                >
                    <label class="active-form__label active-form__label_required" for="attribute-type"
                        >Название справочника</label
                    >

                    <VSelect
                        id="attribute-type"
                        name="type"
                        class="active-form__control"
                        placeholder="Название справочника"
                        taggable
                        label="type"
                        :class="{
                            error: errors[0],
                        }"
                        :options="typeOptions"
                        :disabled="editable?.type === false"
                        @option:selected="setAttribute"
                        v-model="selectedAttribute"
                    />

                    <span class="active-form__error">
                        {{ errors[0] || hiddenTypeError }}
                    </span>
                </ValidationProvider>

                <ValidationProvider
                    class="active-form__field"
                    name="valueType"
                    rules="required|max:64"
                    v-slot="{ errors }"
                    v-if="visibleFields.value_type && !selectedAttribute?.value_type"
                >
                    <label class="active-form__label active-form__label_required" for="attribute-valueType"
                        >Тип значения</label
                    >

                    <VSelect
                        id="attribute-valueType"
                        name="valueType"
                        label="name"
                        class="active-form__control"
                        :class="{
                            error: errors[0],
                        }"
                        :disabled="editable?.value_type === false"
                        :options="valueTypes"
                        :get-option-label="valueTypeTitle"
                        v-model="attribute.value_type"
                    />

                    <span class="active-form__error">
                        {{ errors[0] }}
                    </span>
                </ValidationProvider>

                <ValidationProvider
                    class="active-form__field"
                    name="attribute-value"
                    rules="required|max:255"
                    v-slot="{ errors }"
                    v-if="visibleFields.value"
                >
                    <label
                        class="active-form__label"
                        :class="{ 'active-form__label_required': !nullable }"
                        for="attribute-value"
                        >Значение</label
                    >

                    <!-- input for validation -->
                    <input type="text" v-model="attribute.value" hidden v-if="!nullable" />
                    <VAttributeValue
                        :nullable="nullable"
                        :values="selectedAttribute?.values"
                        v-model="attribute"
                    ></VAttributeValue>

                    <span class="active-form__error">
                        {{ errors[0] }}
                    </span>
                </ValidationProvider>

                <!-- Вызывающая сторона передавала visibleFields.searchable и
                     editable.searchable с самого начала, а самого поля здесь не было:
                     флаг уходил на сервер всегда false, и поиск по серийным номерам
                     не работал ни у кого. -->
                <div class="active-form__field inventory-attribute-modal__searchable" v-if="visibleFields.searchable">
                    <label class="active-form__label" for="attribute-searchable">Участвует в поиске</label>

                    <VSwitchButton
                        id="attribute-searchable"
                        color="var(--color-green)"
                        :disabled="editable?.searchable === false"
                        :value="Boolean(attribute.searchable)"
                        @input="attribute.searchable = $event"
                    ></VSwitchButton>

                    <span class="inventory-attribute-modal__hint">
                        Значение будет находиться в поиске по списку оборудования — так ищут серийные и инвентарные
                        номера.
                    </span>
                </div>

                <div class="active-form__actions">
                    <VButton type="button" class="active-form__action button_green-light" @click="hide">
                        Отмена
                    </VButton>

                    <VButton type="submit" class="active-form__action button_green"> Сохранить </VButton>
                </div>
            </form>
        </ValidationObserver>
    </VModalWrapper>
</template>
<script>
// svg
import TrashSvg from '@/images/trash.svg';

// Components
import VInput from '../../../components/VInput/VInput.vue';
import VButton from '../../../components/VButton/VButton.vue';
import VSelect from '../../../components/VSelect/VSelect.vue';
import VModalWrapper from '../../../components/VModalWrapper/VModalWrapper.vue';
import VContenteditable from '../../../components/VContenteditable/VContenteditable.vue';
import VAttributeValue from '../components/VAttributeValue.vue';
import VSwitchButton from '../../../components/VCheckbox/VSwitchButton.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

// Other
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import ModalNames from '../../../../js/enums/ModalNames';
import ModalMixin from '../../../../js/vue/mixins/ModalMixin';
import InventoryAttributeValueTypes, {
    inventoryAttributeValueTypeTitle,
} from '../../../../js/enums/InventoryAttributeValueTypes';
import { isHiddenDictionaryType, withoutHiddenDictionaries } from '../../../../js/inventoryAttributes';

@Component({
    components: {
        TrashSvg,

        VInput,
        VButton,
        VSelect,
        VModalWrapper,
        VContenteditable,
        VAttributeValue,
        VSwitchButton,

        ValidationObserver,
        ValidationProvider,
    },
})
class VInventoryAttributeCreateModal extends ModalMixin {
    @Prop({ type: String, default: ModalNames.INVENTORY_ATTRIBUTE_CARD })
    name;
    @Prop({ type: String, default: 'Карточка создания атрибута' })
    defaultTitle;
    @Prop({ type: Object, required: true })
    cache;

    editable = {};
    nullable = false;
    attribute = null;
    selectedAttribute = null;
    visibleFields = {};

    valueTypes = InventoryAttributeValueTypes;

    valueTypeTitle(type) {
        return inventoryAttributeValueTypeTitle(type);
    }

    /**
     * Справочники, из которых можно выбрать (7-D3).
     *
     * Города и серийного номера здесь нет: город единицы живёт собственным полем,
     * серийный номер — системное поле формы создания. Предлагать их как дополнительное
     * поле значит предлагать завести второй источник того же факта.
     *
     * Поле остаётся taggable — новое название справочника вводится прямо здесь, — поэтому
     * фильтр списка не единственная застава: набранное вручную системное название
     * отвергает submit(), а последнее слово всё равно за сервером.
     */
    get typeOptions() {
        return withoutHiddenDictionaries(this.cache?.attributes);
    }

    /**
     * Название набрано руками и означает системное поле.
     */
    get isHiddenType() {
        return isHiddenDictionaryType(this.attribute?.type);
    }

    /**
     * Почему такое название не принимается. Показывается сразу при вводе, а не после
     * нажатия «Сохранить»: молча не срабатывающая кнопка выглядит поломкой.
     */
    get hiddenTypeError() {
        if (!this.visibleFields.type || !this.isHiddenType) {
            return '';
        }

        return 'Это системное поле: форма создания спрашивает его сама. Дополнительным атрибутом оно не заводится.';
    }

    setAttribute(attribute) {
        this.attribute = { ...this.attribute, type: attribute.type, value: '' };
    }

    submit() {
        // Системное поле не заводится обычным атрибутом ни выбором, ни ручным вводом:
        // оно уже есть в форме создания своим полем, и второе с тем же смыслом
        // расходилось бы с ним значением.
        if (this.visibleFields.type && this.isHiddenType) {
            return;
        }

        this.resolve(this.attribute);
        this.hide();
    }

    beforeOpen(event) {
        this.nullable = event?.params?.nullable ?? false;
        this.editable = event?.params?.editable ?? {};
        this.attribute = event?.params?.attribute;
        this.visibleFields = event?.params?.visibleFields ?? {};
        this.selectedAttribute = null;
    }

    beforeClose() {
        this.editable = {};
        this.nullable = false;
        this.attribute = null;
        this.selectedAttribute = null;
    }
}

export default VInventoryAttributeCreateModal;
</script>
<style lang="scss">
.inventory-attribute-modal {
    &__searchable {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;

        & .active-form__label {
            margin: 0;
        }
    }

    &__hint {
        flex-basis: 100%;
        color: #9aa0a6;
        font-size: 0.75rem;
        line-height: 1.0625rem;
    }
}
</style>
